import { Injectable } from '@angular/core';
import { map, mergeMap, Observable, tap } from 'rxjs';
import {
  ApiMetadataApiService,
  ComponentOwnership,
  ListComponentOwnershipsQuery,
  UpsertComponentOwnershipRqst,
} from '@xpo-ltl/sdk-apimetadata';
import { EmployeeModel } from '../models/employee.class';
import { HumanResourcesService } from '../../arsenal-components/services/human-resources.service';
import { ComponentOwnershipFields } from '../../enums/component-ownership';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { DropdownBasic } from '../../ensemble-config/models/dropdown-basic.model';
import { FirestoreEnum } from '../../shared/enums/firestore-app.enum';
import { FirestoreAppConfig } from '../../shared/models/firestore-app-config';

@Injectable({
  providedIn: 'root',
})
export class ComponentOwnershipService {
  employees: EmployeeModel[] = [];
  employeeIds: Set<string> = new Set<string>();

  constructor(
    private fs: AngularFirestore,
    private humanResourceService: HumanResourcesService,
    private apiMetadataApiService: ApiMetadataApiService
  ) {}

  listComponentOwnerships(): Observable<ComponentOwnership[]> {
    return this.apiMetadataApiService
      .listComponentOwnerships(new ListComponentOwnershipsQuery(), { loadingOverlayEnabled: false })
      .pipe(mergeMap((list) => this.transformComponents(list.componentOwnerships)));
  }

  getEmployees(): EmployeeModel[] {
    return this.employees;
  }

  upsertComponentOwnership(component: ComponentOwnership): Observable<void> {
    const request = new UpsertComponentOwnershipRqst();
    request.componentOwnership = component;
    return this.apiMetadataApiService.upsertComponentOwnership(request);
  }

  listEmployeesByLastName(lastName: string): Observable<EmployeeModel[]> {
    return this.humanResourceService.listEmployeesByName(lastName).pipe(
      map((employees) => {
        return employees
          .map((emp) => new EmployeeModel(emp.personalData))
          .sort((a, b) => a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()));
      })
    );
  }

  getTechnologyList(): Observable<DropdownBasic[]> {
    return this.fs
      .collection(FirestoreEnum.APP_KEY_CONFIGS)
      .doc<FirestoreAppConfig>(FirestoreEnum.ARSENAL)
      .get()
      .pipe(
        map((doc) =>
          (doc.data()?.appConfigs?.technologies ?? [])
            .map((tech) => {
              return { label: tech, value: tech };
            })
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      );
  }

  private transformComponents(components: ComponentOwnership[]): Observable<ComponentOwnership[]> {
    components.forEach((component) => {
      this.addEmployeeId(component.executiveLeadershipEmployeeId);
      this.addEmployeeId(component.owningTeamManagerEmployeeId);
      this.addEmployeeId(component.owningTeamTechLeadEmployeeId);
      this.addEmployeeId(component.architectEmployeeId);
      this.addEmployeeId(component.platformEngineerEmployeeId);
    });

    return this.listEmployeeIds().pipe(
      tap((employees) => (this.employees = employees)),
      map(() => this.mapComponentsOwnership(components))
    );
  }

  private addEmployeeId(id: string) {
    if (!!id && /\d/.test(id)) {
      this.employeeIds.add(id);
    }
  }

  private findEmployeeName(id: string): string {
    return this.employees?.find((employee) => employee.id === id)?.getDisplayName() || '';
  }

  private listEmployeeIds(): Observable<EmployeeModel[]> {
    return this.humanResourceService
      .listEmployeesById(Array.from(this.employeeIds.values()))
      .pipe(map((employees) => employees.map((employee) => new EmployeeModel(employee))));
  }

  private mapComponentsOwnership(components: ComponentOwnership[]): ComponentOwnership[] {
    return components.map((component) => {
      return {
        ...component,
        [ComponentOwnershipFields.LEADERSHIP_NAME]: this.findEmployeeName(component.executiveLeadershipEmployeeId),
        [ComponentOwnershipFields.PRODUCT_OWNER_NAME]: this.findEmployeeName(component.owningTeamManagerEmployeeId),
        [ComponentOwnershipFields.TECH_LEAD_NAME]: this.findEmployeeName(component.owningTeamTechLeadEmployeeId),
        [ComponentOwnershipFields.ARCHITECT_NAME]: this.findEmployeeName(component.architectEmployeeId),
        [ComponentOwnershipFields.PLATFORM_ENG_NAME]: this.findEmployeeName(component.platformEngineerEmployeeId),
        owningTeam: component.owningTeam?.replace(/-/g, '‑'),
        technologyList: component.technologyList,
      };
    });
  }
}
